import { useState } from 'react'
import { Field, FormError, PrimaryButton, TextInput } from '../components/form'
import { useSession } from '../lib/session'

/**
 * Sign-in for the platform panel.
 *
 * It says nothing about platform access — whether this account has any is decided
 * after the credentials check, and hinting either way here would tell an attacker
 * which addresses are worth attacking.
 */
export function Login() {
  const { signIn } = useSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await signIn(email, password)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center p-4">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-bg" />

      <div className="card w-full max-w-sm p-7">
        <div className="mb-6 flex items-center gap-3">
          <div className="brand-mark h-11 w-11" aria-hidden="true" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-ink">Modulus</h1>
            <p className="text-sm text-muted">Platform</p>
          </div>
        </div>

        <form onSubmit={(event) => void submit(event)} className="space-y-4">
          <Field label="Email">
            <TextInput
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@modulus.study"
            />
          </Field>
          <Field label="Password">
            <TextInput
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Field>

          <FormError message={error} />

          <PrimaryButton type="submit" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </PrimaryButton>
        </form>

        <p className="mt-5 text-xs text-faint">
          Sessions here end when the tab closes, and idle for 30 minutes signs you out.
        </p>
      </div>
    </div>
  )
}
